import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../components";
import Calendar from "react-calendar";
import AlertService from "../../service/AlertService";
import EmailService from "../../service/EmailService";
import SmsService from "../../service/SmsService";
import ApiService from "../../service/ApiService";
import ToastMessages from "../../model/enum/ToastMessages";
import Appointment from "../../model/appointment";
import Status from "../../model/status";
import User from "../../model/user";
import Client from "../../model/client";
import DB_Status from "../../model/enum/DBStatus";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import ClientModal from "../client/ClientModal";
import DeleteModal from "../modal/DeleteModal";
import TimePicker from "react-time-picker";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "../../assets/admin/edit-icon.svg";
import PageResultResponse from "../../model/pageResultResponse";
import Select, { StylesConfig } from "react-select";
import useStyles from "./styles";
import styled from "styled-components";
import {
  DarkGrey,
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../shared/styles/colors";
import Misc from "../../utils/Misc";

export const CalendarWrapper = styled.div`
  .react-calendar__navigation__label,
  .react-calendar__navigation__arrow {
    background-color: white; /* Custom color */
    color: black; /* Button text color */
    border: none; /* Remove default border */
    padding: 5px 10px;
    border-radius: 10px;
    cursor: pointer;
  }

  .react-calendar__navigation__arrow:hover {
    background-color: #0056b3; /* Darker shade on hover */
  }

  .react-calendar__tile--active,
  .react-calendar__tile--active:after {
    color: white; /* Text color for active date */
    background: #1e2e55;
    border-radius: 20px; /* Optional: make the button rounded */
  }

  .react-calendar__tile--active:hover {
    background-color: #1e2e5580; /* Darker color on hover */
  }
`;

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

const selectStyles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "95% !important",
    borderColor: DarkGrey,
    borderRadius: "5px",
    marginLeft: "10px",
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : DarkGrey,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
      ? PrimaryColor
      : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

const AppointmentModal = ({ appointment, resetList }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = appointment != null;
  const id = edit ? appointment.id : 0;
  const [scheduledAt, setScheduledAt] = useState(
    edit ? new Date(appointment.scheduledAt) : new Date()
  );
  const [start, setStart] = useState(edit ? appointment.initialTime : "08:00");
  const [end, setEnd] = useState(edit ? appointment.finalTime : "09:00");
  const [notes, setNotes] = useState(edit ? appointment.notes : null);
  const [status, setStatus] = useState(edit ? appointment.Status : null);
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [sellerId, setSellerId] = useState(
    edit && appointment.userId ? appointment.userId : null
  );
  const [sellers, setSellers] = useState<User[]>([]);
  const [client, setClient] = useState(edit ? appointment.Client : null);
  const [clients, setClients] = useState<Client[]>([]);
  const [notify, setNotify] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch all
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [fetchClients, fetchSellers, fetchStatus] = await Promise.all([
          ApiService.get<PageResultResponse>(DatabaseEntities.CLIENTS),
          ApiService.get<PageResultResponse>(
            `${DatabaseEntities.USERS}?roleId=4`
          ),
          ApiService.get<Status[]>(
            `${DatabaseEntities.STATUS}?type=General`
          ),
        ]);
        if (Misc.isArrayEmpty(fetchClients.items)) return;
        if (Misc.isArrayEmpty(fetchSellers.items)) return;
        if (Misc.isArrayEmpty(fetchStatus)) return;
        setClients(fetchClients.items);
        setSellers(fetchSellers.items);
        setStatusList(fetchStatus);
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };
    if (open) fetchData();
  }, [open]);

  const handleClient = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setClient(null);
      return;
    }

    // Otherwise, e is an object with value and label properties
    let value = e.value;
    var clt = clients.find((x) => x.id == value);
    setClient(clt);
  };

  const handleNotes = (e: any) => setNotes(e.target.value);

  const handleStatus = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setStatus(null);
      return;
    }

    let value = e.value;
    var stt = statusList.find((x) => x.id == value);
    setStatus(stt);
  };

  const handleSeller = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setSellerId(null);
      return;
    }

    setSellerId(e.value);
  };

  const handleNotification = () => setNotify((notify) => !notify);
  const handleStart = (value: any) => setStart(value);
  const handleEnd = (value: any) => setEnd(value);

  const toLocalDateOnly = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const handleCreate = async (payload: Appointment) => {
    await ApiService.post<Appointment>(
      DatabaseEntities.APPOINTMENTS,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.CREATE_FAILED);
      });
  };

  const handleUpdate = async (payload: Appointment) => {
    await ApiService.put<Appointment>(
      `${DatabaseEntities.APPOINTMENTS}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Appointment) => {
    await ApiService.delete<any>(
      `${DatabaseEntities.APPOINTMENTS}/${payload.id}`
    )
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = () => {
    return (
      scheduledAt === undefined ||
      start === "" ||
      end === "" ||
      client === null ||
      client === "" ||
      status === null ||
      status === ""
    );
  };

  const register = async () => {
    if (invalidInput()) return;

    const payload: Appointment = {
      id: id,
      scheduledAt: toLocalDateOnly(scheduledAt),
      initialTime: start,
      finalTime: end,
      notes: notes,
      clientId: client?.id,
      userId: sellerId,
      statusId: status?.id,
    };

    if (edit) {
      await handleUpdate(payload)
        .then(async () => {
          if (notify) {
            await handleEmailAndSmsSend();
          }
        })
        .catch((error: any) => {
          AlertService.error(
            `${ToastMessages.APPOINTMENT_UPDATE_FAILED}: ${error}`
          );
        });
    } else {
      await handleCreate(payload)
        .then(async () => {
          if (notify) {
            await handleEmailAndSmsSend();
          }
        })
        .catch((error: any) => {
          AlertService.error(`${ToastMessages.APPOINTMENT_FAILED}: ${error}`);
        });
    }
    handleClose();
  };

  const handleEmailAndSmsSend = async () => {
    let type: string = "";

    switch (status.name) {
      case DB_Status.ACCEPTED:
        type = "Appointment Confirmed";
        break;
      case DB_Status.CANCELED:
        type = "Appointment Denied";
        break;
      case DB_Status.COMPLETED:
        type = "Service Completed";
        break;
      case DB_Status.ESTIMATE_APPROVED:
        type = "Estimate Approved";
        break;
      case DB_Status.SCHEDULE_START_JOB:
        type = "Schedule to start job";
        break;
      default:
        break;
    }

    if (type != "") {
      await SmsService.send(client.phone, type, start, end, scheduledAt)
        .then(() => {
          AlertService.success(ToastMessages.SMS_SENT);
        })
        .catch((error) => {
          AlertService.error(`${ToastMessages.SMS_NOT_SENT}: ${error}`);
        });
      await EmailService.sendEmailToUser(
        client.firstName,
        client.email,
        type,
        scheduledAt,
        start,
        end
      )
        .then(() => {
          AlertService.success(ToastMessages.EMAIL_SENT_TO_CLIENT);
        })
        .catch((error) => {
          AlertService.error(`${ToastMessages.EMAIL_NOT_SENT_TO_CLIENT}: ${error}`);
        });
    }
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Appointment
        </Btn>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              {edit ? (
                <Typography className={classes.title}>
                  <DeleteModal item={appointment} handleDelete={handleDelete} />
                </Typography>
              ) : (
                <Typography className={classes.title}>
                  Schedule appointment
                </Typography>
              )}
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.form}>
              <div className={classes.container}>
                <div className={classes.inputContainer}>
                  <div className={classes.inputGroup}>
                    <Select
                      styles={selectStyles}
                      value={
                        client?.id
                          ? {
                              value: client.id,
                              label: `${client.firstName} ${client.lastName}`,
                            }
                          : ""
                      }
                      isClearable={true}
                      onChange={handleClient}
                      options={clients.map((clt: Client) => ({
                        value: clt.id,
                        label: `${clt.firstName} ${clt.lastName}`,
                      }))}
                      placeholder="Select Client"
                    />
                    <ClientModal
                      externalService={true}
                      resetList={() => setClients([])}
                      setClient={(clt: Client) => setClient(clt)}
                    />
                  </div>
                  <div className={classes.inputGroup}>
                    <TimePicker
                      className={classes.input}
                      onChange={handleStart}
                      value={start}
                      clearIcon={null}
                      clockIcon={null}
                      shouldOpenClock={() => false}
                    />
                    <TimePicker
                      className={classes.input}
                      onChange={handleEnd}
                      value={end}
                      clearIcon={null}
                      clockIcon={null}
                      shouldOpenClock={() => false}
                    />
                  </div>
                  <div className={classes.selectDiv}>
                    <Select
                      styles={selectStyles}
                      value={
                        sellerId
                          ? {
                              value: sellerId,
                              label: `${
                                sellers.find(
                                  (s: User) => s.id === sellerId
                                )?.firstName
                              } ${
                                sellers.find(
                                  (s: User) => s.id === sellerId
                                )?.lastName
                              }`,
                            }
                          : ""
                      }
                      isClearable={true}
                      onChange={handleSeller}
                      options={sellers.map((sllr: User) => ({
                        value: sllr.id,
                        label: `${sllr.firstName} ${sllr.lastName}`,
                      }))}
                      placeholder="Select Seller"
                    />
                  </div>
                  <div className={classes.selectDiv}>
                    <Select
                      styles={selectStyles}
                      value={
                        status?.id
                          ? {
                              value: status.id,
                              label: `${status.name}`,
                            }
                          : ""
                      }
                      isClearable={true}
                      onChange={handleStatus}
                      options={statusList.map((stts: Status) => ({
                        value: stts.id,
                        label: `${stts.name}`,
                      }))}
                      placeholder="Select Status"
                    />
                  </div>
                  <textarea
                    className={classes.textarea}
                    placeholder="Add some notes..."
                    onChange={handleNotes}
                    value={notes}
                  />
                  <div className={classes.inputGroup}>
                    <input
                      type="checkbox"
                      className={classes.checkbox}
                      onChange={handleNotification}
                      checked={notify}
                    />
                    <Typography className={classes.checkboxtext}>
                      Send notification{" "}
                    </Typography>
                  </div>
                </div>
                <div
                  className={`calendar-container ${classes.calendarContainer}`}
                >
                  <CalendarWrapper>
                    <Calendar
                      className={classes.calendar}
                      calendarType={"US"}
                      value={scheduledAt}
                      tileDisabled={({ date }) => date.getDay() === 6} //disabled on saturdays
                      onChange={(date: any) => setScheduledAt(date)}
                    />
                  </CalendarWrapper>
                </div>
              </div>
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={register}
                disabled={invalidInput()}
              >
                Schedule now
              </Btn>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default AppointmentModal;
